/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import Framework, { BaseService } from '@greenville/framework';
import * as constants from '../../../../common/constants';
import utils from '../../../../common/utils';
import handleEvalReportError, { resolveServerErrorMessage } from './evalErrors';
import notifyRubrics from './rubricsNotifications';
import { buildRubricsCreatePath } from './rubricsApiPaths';

/**
 * POST /rubrics — creates the rubric from name, types and rubric text.
 * Sampling and schedule are not accepted here; they only exist on the scheduler endpoint, so the
 * returned rubricId is what makes the scheduler step possible.
 */
export default class RubricsCreateService extends BaseService {
  constructor() {
    super(constants.CREATE_RUBRICS_RUBRIC, 'RubricsCreateStatus');
  }

  async handleEvent(eventName, event) {
    const { graphId, tenantId, requestBody } = event;
    const headers = utils.buildHeadersWithTenantId({ tenantId });
    const url = buildRubricsCreatePath({ graphId });
    const { isStackB } = utils.getStackBParams();

    if (isStackB) {
      const enhancedHeaders = await utils.resolveStackBHeaders(headers);
      return this.getClient('aiadminstackb').post(url, requestBody, { headers: enhancedHeaders });
    }
    return this.getClient('aiadmin').post(url, requestBody, { headers });
  }

  handleResponse(response) {
    const { data } = response;
    const store = Framework.getStoreRegistry().getStore('rubricsData');

    if (data?.success) {
      notifyRubrics({ type: 'success', langMessage: 'rubrics.CREATE_SUCCESS' });
      store.completeCreate(data);
      return;
    }

    notifyRubrics({ type: 'error', message: resolveServerErrorMessage(data), langMessage: 'rubrics.CREATE_ERROR' });
    store.failCreate();
  }

  handleError(error) {
    Framework.getStoreRegistry().getStore('rubricsData').failCreate();
    handleEvalReportError(error, 'rubrics.CREATE_ERROR');
  }
}
